import React, { useState, useEffect, useRef } from 'react';
import http from '../../lib/http';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ChevronLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ChatBubbleBottomCenterTextIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

const ShareCandidateReviewModal = ({
  isOpen,
  onClose,
  onBack,
  screeningId,
  selectedCandidates = [],
  screening,
  onRefresh,
}) => {
  const [shareLink, setShareLink] = useState(null);
  const [shareCode, setShareCode] = useState(null);
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Tracks the exact candidate-id set we already minted a link for, so that a
  // parent refresh (which hands us a new array reference with the same ids to
  // surface fresh hiring-manager decisions) does not re-POST and re-mint the link.
  const generatedSignatureRef = useRef(null);

  const hasCandidates = selectedCandidates.length > 0;
  // Only claim candidates were "shortlisted / evaluated on interviews" when that
  // is actually true for every shared candidate. This modal is also reached from
  // the Candidates tab (per-row + bulk) where candidates may not be selected yet.
  const allShortlisted =
    hasCandidates && selectedCandidates.every((c) => Boolean(c.shortlisted));
  const candidateNoun = allShortlisted ? 'shortlisted candidate' : 'candidate';

  const handleRefresh = async () => {
    if (typeof onRefresh !== 'function' || refreshing) return;
    try {
      setRefreshing(true);
      await onRefresh();
    } catch (err) {
      // parent is expected to handle errors; fail quietly here
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      // Reset so the next fresh open regenerates.
      generatedSignatureRef.current = null;
      return;
    }
    const candidateIds = selectedCandidates.map((c) => c.id || c._id).filter(Boolean);
    if (candidateIds.length === 0) return;
    const signature = [...candidateIds].sort().join(',');
    // Same candidate set as the link we already minted this open — skip (a parent
    // refresh only needs to refresh decisions via props, not re-create the link).
    if (signature === generatedSignatureRef.current) return;
    generateShareLink(candidateIds, signature);
  }, [isOpen, selectedCandidates]);

  const generateShareLink = async (candidateIds, signature) => {
    try {
      setLoading(true);
      const res = await http.post(`/api/screenings/${screeningId}/candidate-review-share-links`, {
        candidateIds,
      });
      setShareLink(res.data.shareLink);
      setShareCode(res.data.shareCode);
      setIsActive(res.data.isActive);
      generatedSignatureRef.current = signature;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to generate share link');
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async () => {
    if (!shareCode) return;
    try {
      setToggling(true);
      const newStatus = !isActive;
      await http.patch(
        `/api/screenings/${screeningId}/candidate-review-share-links/${shareCode}`,
        { isActive: newStatus }
      );
      setIsActive(newStatus);
      toast.success(newStatus ? 'Link activated' : 'Link deactivated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to toggle link status');
    } finally {
      setToggling(false);
    }
  };

  const copyLink = () => {
    if (!shareLink) return;
    navigator.clipboard.writeText(shareLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
    toast.success('Link copied to clipboard');
  };

  const copyEmailDraft = () => {
    const emailText = getEmailDraft();
    navigator.clipboard.writeText(emailText);
    setEmailCopied(true);
    setTimeout(() => setEmailCopied(false), 2000);
    toast.success('Email draft copied to clipboard');
  };

  const getEmailDraft = () => {
    const jobTitle = screening?.roleName || screening?.jobId?.title || '[Job Title]';
    const companyName =
      screening?.companyName || screening?.jobId?.companyName || '[Company Name]';
    const screeningName = screening?.name || 'Screening';

    const contextSentence = allShortlisted
      ? `These candidates have successfully completed our ${screeningName} and have been evaluated based on their resumes, assessments, and interviews. I believe they are strong matches for this role and would appreciate your review and feedback.`
      : `These candidates are part of our ${screeningName}. I'd appreciate your review and feedback on their profiles.`;

    return `Subject: Review Candidates - ${jobTitle}

Dear Hiring Manager,

I hope this message finds you well. I'm writing to share a curated list of ${selectedCandidates.length} ${candidateNoun}${selectedCandidates.length !== 1 ? 's' : ''} for the ${jobTitle} position at ${companyName}.

${contextSentence}

Please use the following secure link to review the candidates:
${shareLink || '[Link will be generated]'}

On this page, you can:
• View detailed candidate profiles, scores, and resumes
• Add your decision (Approve/Pending/Reject) for each candidate
• Include notes or feedback for the recruitment team

The link is currently ${isActive ? 'active' : 'inactive'} and can be accessed without login. Please let me know if you have any questions or need additional information about any of the candidates.

Looking forward to your feedback.

Best regards,
[Your Name]`;
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between flex-shrink-0 px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-violet-50 via-white to-violet-50">
          <div className="flex items-center gap-3">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="text-gray-400 hover:text-gray-600 mr-1"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
            )}
            <div className="space-y-1">
              <h3 className="text-lg font-semibold text-gray-900 tracking-tight">
                Share Candidate Review
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Generate a secure link to share {selectedCandidates.length} {candidateNoun}
                {selectedCandidates.length !== 1 ? 's' : ''} with the hiring manager
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {typeof onRefresh === 'function' && (
              <button
                type="button"
                onClick={handleRefresh}
                disabled={refreshing}
                title="Refresh hiring manager decisions"
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-violet-700 hover:bg-violet-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>{refreshing ? 'Refreshing…' : 'Refresh'}</span>
              </button>
            )}
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto px-6 py-5 flex flex-col gap-5 bg-slate-50/60">
          {!hasCandidates ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
              <XCircleIcon className="h-8 w-8 text-gray-400" />
              <p className="text-sm font-medium text-gray-700">No candidates available to share</p>
              <p className="text-xs text-gray-500 max-w-sm">
                The selected candidates may have been moved, removed, or are no longer in this list.
                Please close this window and try again.
              </p>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="inline-block w-8 h-8 border-4 border-violet-600 border-t-transparent rounded-full animate-spin" />
              <p className="ml-3 text-gray-600">Generating share link...</p>
            </div>
          ) : (
            <>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-900">Share Link Status</h4>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isActive}
                    disabled={toggling}
                    onClick={toggleActive}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                      isActive ? 'bg-violet-600' : 'bg-gray-200'
                    }`}
                  >
                    {isActive && (
                      <span
                        className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-white"
                        aria-hidden
                      >
                        <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} />
                      </span>
                    )}
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                        isActive ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
                <p className={`text-sm ${isActive ? 'text-violet-600' : 'text-gray-500'}`}>
                  Link is {isActive ? 'active' : 'inactive'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {isActive
                    ? 'Anyone with this link can view the candidates and make decisions'
                    : 'This link is currently disabled and cannot be accessed'}
                </p>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Shareable Link
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={shareLink || (loading ? 'Generating...' : 'Unavailable — try refreshing')}
                    className="flex-1 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700 font-mono"
                  />
                  <button
                    type="button"
                    onClick={copyLink}
                    disabled={!shareLink}
                    className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    {linkCopied ? (
                      <>
                        <CheckIcon className="h-4 w-4" />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <ClipboardDocumentIcon className="h-4 w-4" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Share this link with the hiring manager via email, Slack, or any messaging
                  platform
                </p>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-semibold text-gray-900">
                    Email Draft Template
                  </label>
                  <button
                    type="button"
                    onClick={copyEmailDraft}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
                  >
                    {emailCopied ? (
                      <>
                        <CheckIcon className="h-3.5 w-3.5" />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                        <span>Copy Email</span>
                      </>
                    )}
                  </button>
                </div>
                <textarea
                  readOnly
                  value={getEmailDraft()}
                  rows={14}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs font-mono bg-gray-50 text-gray-700 resize-none focus:outline-none"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Copy this template and customize it before sending to the hiring manager
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">
                  What the hiring manager can do:
                </h4>
                <ul className="space-y-1.5 text-sm text-blue-800">
                  <li className="flex items-start gap-2">
                    <CheckIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>View all {selectedCandidates.length} {allShortlisted ? 'shortlisted ' : ''}candidates with full scores &amp; resume</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>Make decisions (Selected / Offered / Pending / Rejected) for each candidate</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>Add notes and feedback visible to you as the recruiter</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>No login required — instant access via the link</span>
                  </li>
                </ul>
              </div>

              {/* HM Decisions summary — shown if any candidate already has a decision */}
              {selectedCandidates.some((c) => c.decisionStatus) && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <ChatBubbleBottomCenterTextIcon className="h-4 w-4 text-violet-600" />
                    Hiring Manager Feedback
                  </h4>
                  <div className="space-y-3">
                    {selectedCandidates
                      .filter((c) => c.decisionStatus)
                      .map((c) => {
                        const name = c.name || c.email || 'Candidate';
                        const statusConfig = {
                          selected: { label: 'Selected', cls: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' },
                          pending: { label: 'Pending', cls: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200' },
                          rejected: { label: 'Rejected', cls: 'bg-red-50 text-red-700 ring-1 ring-red-200' },
                          offered: { label: 'Offered', cls: 'bg-violet-50 text-violet-700 ring-1 ring-violet-200' },
                        };
                        const conf = statusConfig[c.decisionStatus] || { label: c.decisionStatus, cls: 'bg-gray-100 text-gray-600' };
                        return (
                          <div key={c.id || c._id} className="flex flex-col gap-1 pb-3 last:pb-0 border-b last:border-b-0 border-gray-100">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm font-medium text-gray-900 truncate">{name}</span>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${conf.cls}`}>
                                {conf.label}
                              </span>
                            </div>
                            {c.decisionNotes && (
                              <p className="text-xs text-gray-500 leading-relaxed">{c.decisionNotes}</p>
                            )}
                            {c.decisionAt && (
                              <p className="text-[11px] text-gray-400">
                                {new Date(c.decisionAt).toLocaleString()}
                              </p>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex-shrink-0 flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-white">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareCandidateReviewModal;
